import React from "react";
import { MemberEventAttendee } from "../../../interfaces/models/IMemberEventAttendee";
import { specialCountries } from "../../../utils/const";
import "../Events.css";
import CircularPhoto from "../../../components/Atomos/CircularPhoto/CircularPhoto";
import {
  buildFirmProfilePath,
  buildPersonProfilePath,
} from "../../../utils/publicProfileUrls";

interface AttendeeContactCardProps {
  attendee: MemberEventAttendee;
}

const AttendeeContactCard: React.FC<AttendeeContactCardProps> = ({
  attendee,
}) => {
  // Records without the flag predate it and are treated as searchable, matching Member Search.
  const isPersonSearchable = attendee.person.isSearchable !== false;
  const personPath = isPersonSearchable
    ? buildPersonProfilePath(
        attendee.person.name,
        attendee.person.lastName,
        attendee.person.legacyId
      )
    : null;
  const firmPath = buildFirmProfilePath(
    attendee.firm?.name,
    attendee.firm?.legacyId
  );

  return (
    <div className="p-4">
      <div className="flex items-start space-x-4">
        <CircularPhoto
          personId={attendee.person.id}
          photo={attendee.person.photo || null}
          alt={`${attendee.person.name} ${attendee.person.lastName}`}
          className="w-[90px] h-[90px]"
          containerClassName="flex-shrink-0"
          objectPosition="center 10%"
        />

        <div className="flex-1">
          <div className="flex gap-2 items-start">
            {personPath ? (
              <a
                href={personPath}
                className="attendee-name hover:text-red-600 hover:underline transition-colors text-left leading-5"
              >
                {attendee.person.name} {attendee.person.lastName}
              </a>
            ) : (
              <span className="attendee-name text-left leading-5">
                {attendee.person.name} {attendee.person.lastName}
              </span>
            )}

            {attendee.person.firmEmail && (
              <>
                <div className="w-[1px] h-4 bg-black mt-[3px]"></div>
                <button
                  onClick={() =>
                    window.open(`mailto:${attendee.person.firmEmail}`, "_blank")
                  }
                  className="w-5 h-5 bg-blue-600 text-white rounded-full hover:bg-red-600 transition-colors flex items-center justify-center flex-shrink-0 mt-[1px]"
                  title={`Email ${attendee.person.name} ${attendee.person.lastName}`}
                >
                  <svg
                    className="w-3 h-3"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                </button>
              </>
            )}
          </div>

          <p className="attendee-title mb-2 text-left italic">
            {attendee.person.unlistedTitle ||
              attendee.person.title ||
              "Partner"}
          </p>

          <div className="space-y-0">
            {firmPath ? (
              <a
                href={firmPath}
                className="attendee-firm hover:text-red-600 hover:underline transition-colors text-left block"
              >
                {attendee.firm?.name}
              </a>
            ) : (
              <span className="attendee-firm text-left block">
                {attendee.firm?.name || "No firm found"}
              </span>
            )}

            <p className="attendee-services text-left">
              {attendee.firm?.serviceType === "Other"
                ? "Legal Services"
                : attendee.firm?.serviceType || "Legal Services"}
            </p>

            {attendee.firm?.country && (
              <p className="attendee-address text-left italic">
                {specialCountries.includes(attendee.firm.countryName || "")
                  ? attendee.firm.stateName + ", " + attendee.firm.countryName
                  : attendee.firm.countryName}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendeeContactCard;
